/**
 * The chart's copy lines say each thing once. A served caption, takeaway or
 * source that repeats the chart title, the host section's heading or an
 * earlier copy line is dropped, so the reader never meets the same sentence
 * twice around one plot.
 */
import type React from 'react';
import { plainHeadingText, useInheritedHostedHeading } from '../fmHostedHeading';
import { useLanguage } from '@/i18n';

export interface FmChartCopy {
  takeaway: React.ReactNode;
  contextDetail?: React.ReactNode;
  caption: React.ReactNode;
  source?: React.ReactNode;
}

/** Copy lines with duplicates of an earlier line (or of a heading) removed. */
export function dedupeChartCopy(
  copy: FmChartCopy,
  headings: readonly (React.ReactNode | null | undefined)[],
): FmChartCopy {
  const seen = new Set<string>();
  headings.forEach((heading) => {
    const text = plainHeadingText(heading ?? null);
    if (text !== null) seen.add(text);
  });
  const keep = (node: React.ReactNode): React.ReactNode => {
    const text = plainHeadingText(node);
    if (text === null) return node;
    if (seen.has(text)) return null;
    seen.add(text);
    return node;
  };
  return {
    takeaway: keep(copy.takeaway),
    contextDetail: keep(copy.contextDetail),
    caption: keep(copy.caption),
    source: keep(copy.source),
  };
}

/**
 * Localized defaults and de-duplicated copy: a caption, takeaway or source
 * that repeats the title or the host section's heading is not printed again.
 */
export function useChartBlockCopy(args: {
  title: string;
  emptyText?: string;
  explanationTitle?: string;
  copy: FmChartCopy;
}): FmChartCopy & { emptyText: string; explanationTitle: string } {
  const { t } = useLanguage();
  const host = useInheritedHostedHeading();
  return {
    ...dedupeChartCopy(args.copy, [args.title, host?.title]),
    emptyText: args.emptyText ?? t.common.ui.chart.dataUnavailable,
    explanationTitle: args.explanationTitle ?? t.common.ui.chart.howToRead,
  };
}
